<template>
  <div class="what-we-are-doing-container">
    <!-- 左侧图片区域 -->
    <div class="left-image-wrapper">
      <img
        :src="leftImage"
        alt="Artificial Hemoglobin Product"
        class="left-image"
      />
      <div class="text-overlay">
        <h2>{{ t("home.coreMedical.title") }}</h2>
        <p class="subtitle">
          {{ t("home.coreMedical.subtitle") }}
        </p>
      </div>
    </div>

    <!-- 右侧内容区域 -->
    <div class="right-content-wrapper">
      <!-- 视频背景 -->
      <video autoplay muted loop playsinline class="background-video">
        <source :src="hemoglobinVideo" type="video/mp4" />
        {{ t("home.coreMedical.videoNotSupported") }}
      </video>

      <div class="video-overlay"></div>

      <div class="content-area">
        <div class="features-list">
          <div
            v-for="(feature, index) in features"
            :key="index"
            class="feature-item"
          >
            <div class="icon-box">
              <div class="feature-icon">
                <div class="cirle"></div>
              </div>
              <div class="line"></div>
            </div>
            <p>{{ t(`home.coreMedical.features.${index}`) }}</p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from "vue";
import { useI18n } from "vue-i18n";

import leftImage from "../../assets/image/home/cellfree.webp";
import hemoglobinVideo from "../../assets/video/hemoglobin.mp4";

const { t } = useI18n();

const features = ref(["feature1", "feature2", "feature3", "feature4"]);

onMounted(() => {
  const video = document.querySelector(".background-video");
  if (video) {
    video.load();
    video.play().catch((e) => {
      console.log(t("coreMedical.videoPlayError"), e);
    });
  }
});
</script>

<style scoped>
.what-we-are-doing-container {
  display: flex;
  width: 100%;
  height: 100vh;
  overflow: hidden;
  background-color: #000;
}

.left-image-wrapper {
  flex: 1;
  position: relative;
  background-color: #000;
  overflow: hidden;
}

.left-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  filter: brightness(0.7);
  transition: transform 0.5s ease;
}

.left-image-wrapper:hover .left-image {
  transform: scale(1.05);
}

.text-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;
  padding: 2rem;
  box-sizing: border-box;
  background: rgba(0, 0, 0, 0.3);
}

.text-overlay h2 {
  font-size: 3rem;
  font-weight: 500;
  margin-bottom: 1.5rem;
  color: white;
  text-shadow: 2px 2px 8px rgba(0, 0, 0, 0.7);
}

.text-overlay .subtitle {
  font-size: 1.4rem;
  color: rgba(255, 255, 255, 0.9);
  max-width: 80%;
  line-height: 1.6;
  text-shadow: 1px 1px 4px rgba(0, 0, 0, 0.5);
}

.right-content-wrapper {
  flex: 1;
  position: relative;
  background-color: #000;
  overflow: hidden;
}

.background-video {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  z-index: 1;
}

.video-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  z-index: 2;
}

/* 内容区域 */
.content-area {
  position: relative;
  height: 100%;
  display: flex;
  align-items: center;
  padding: 4rem;
  color: white;
  z-index: 3;
}

.features-list {
  width: 100%;
}

.feature-item {
  display: flex;
  align-items: flex-start;
  padding: 1.5rem;
  gap: 1.5rem;
  transition: transform 0.3s ease, background 0.3s ease;
}

.icon-box {
  align-items: center;
  display: flex;
  flex-direction: column;
  gap: 1.6rem;
  justify-content: center;
}

.feature-icon {
  backdrop-filter: blur(44px);
  background: #ffffff1a;
  border-radius: 50%;
  height: 2.8rem;
  min-height: 2.8rem;
  min-width: 2.8rem;
  width: 2.8rem;
  position: relative;
}
.feature-icon .cirle {
  border: 1px solid hsla(0, 0%, 100%, 0.78);
  border-radius: 50%;
  bottom: 0;
  height: 52%;
  left: 0;
  margin: auto;
  max-height: 100%;
  max-width: 100%;
  position: absolute;
  right: 0;
  top: 0;
  width: 52%;
}
.line {
  position: absolute;
  background: #ffffff45;
  height: 30%;
  width: 1px;
  margin-left: 1rem;
  margin-right: 1rem;
  top: 4.8rem;
  left: 2rem;
}
.feature-item .line {
  opacity: 1;
}

.feature-item:last-child .line {
  opacity: 0;
}

.feature-item p {
  font-size: 1.2rem;
  line-height: 1.6;
  color: white;
  margin: 0;
}

/* 响应式设计 */
@media (max-width: 1200px) {
  .what-we-are-doing-container {
    flex-direction: column;
    height: auto;
  }

  .left-image-wrapper,
  .right-content-wrapper {
    flex: none;
    height: 50vh;
    min-height: 400px;
  }

  .content-area {
    padding: 2rem;
  }

  .text-overlay h2 {
    font-size: 2.5rem;
  }

  .text-overlay .subtitle {
    font-size: 1.1rem;
  }
}

@media (max-width: 768px) {
  .content-area {
    padding: 1.5rem;
  }
  .line {
    margin-left: 0.3rem;
  }

  .text-overlay h2 {
    font-size: 2rem;
  }
  .left-image-wrapper {
    height: 20vh;
    min-height: 200px;
  }
  .text-overlay .subtitle {
    font-size: 1.2rem;
    max-width: 90%;
  }

  .feature-item {
    margin-bottom: 1.5rem;
    padding: 1rem;
    flex-direction: row;
    align-items: center;
  }

  .feature-icon {
    margin-right: 0;
    margin-bottom: 0.5rem;
  }

  .feature-item p {
    font-size: 1.2rem;
  }
}

@media (max-width: 480px) {
  .text-overlay h2 {
    font-size: 1.8rem;
  }

  .text-overlay .subtitle {
    font-size: 1.2rem;
  }
  .line {
    margin-left: 0.3rem;
  }
  .left-image-wrapper {
    height: 20vh;
    min-height: 200px;
  }
  .feature-item p {
    font-size: 1.2rem;
  }
}

/* 备用样式 - 如果视频无法加载 */
.right-content-wrapper.video-fallback {
  background: linear-gradient(135deg, #1a2a6c, #b21f1f, #fdbb2d);
}

/* 动画效果 */
@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.feature-item {
  animation: fadeInUp 0.5s ease forwards;
}

.feature-item:nth-child(1) {
  animation-delay: 0.1s;
}
.feature-item:nth-child(2) {
  animation-delay: 0.2s;
}
.feature-item:nth-child(3) {
  animation-delay: 0.3s;
}
.feature-item:nth-child(4) {
  animation-delay: 0.4s;
}
</style>
